import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OsType } from 'shared/enums/os-type.enum';
import { UserSettingType } from 'shared/interfaces/user-settings/user-setting-type.enum';
import { PlatformFilterOsType } from 'src/components/PlatformFilter/platform-filter-props.interface';
import { useUserSettingsContext } from 'src/contexts/UserSettingsContext/UserSettingsContext';

/**
 * Accepts only the game platforms we support in the UI.
 */
const normalizeGamesPlatformOsTypeId = (osTypeId?: number | null): PlatformFilterOsType | null => {
  if (osTypeId === OsType.Ios || osTypeId === OsType.Android) {
    return osTypeId;
  }

  return null;
};

/**
 * Infers the user's phone OS from the browser user agent.
 */
export const detectGamesPlatformOsType = (): PlatformFilterOsType | null => {
  if (typeof navigator === 'undefined') {
    return null;
  }

  if (/android/i.test(navigator.userAgent)) {
    return OsType.Android;
  }

  if (
    /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
  ) {
    return OsType.Ios;
  }

  return null;
};

/**
 * Resolves and persists the platform used to filter game offers.
 */
export const useGamesPlatformPreference = () => {
  const { userSettings, userSettingsLoaded, saveUserSettings } = useUserSettingsContext();
  // In-session filter picks win; otherwise current iOS/Android device wins over the saved profile setting.
  const detectedOsTypeId = useMemo(() => detectGamesPlatformOsType(), []);
  const savedOsTypeId = normalizeGamesPlatformOsTypeId(userSettings?.gamesPlatformOsTypeId);
  const onboardingOsTypeId = detectedOsTypeId ?? OsType.Ios;
  const onboardingCompleted = true;
  // Keeps the UI unblocked immediately after selection while the save request is in flight.
  const autoSavedDetectedOsTypeIdRef = useRef<PlatformFilterOsType | null>(null);
  const [sessionOsTypeId, setSessionOsTypeId] = useState<PlatformFilterOsType | null>(null);
  const [osTypeId, setLocalOsTypeId] = useState<PlatformFilterOsType>(onboardingOsTypeId);

  useEffect(() => {
    if (!onboardingCompleted) {
      setLocalOsTypeId(onboardingOsTypeId);
      return;
    }

    setLocalOsTypeId(sessionOsTypeId ?? detectedOsTypeId ?? savedOsTypeId ?? onboardingOsTypeId);
  }, [detectedOsTypeId, onboardingCompleted, onboardingOsTypeId, savedOsTypeId, sessionOsTypeId]);

  useEffect(() => {
    if (!userSettingsLoaded || savedOsTypeId != null || sessionOsTypeId != null || detectedOsTypeId == null) {
      return;
    }

    if (autoSavedDetectedOsTypeIdRef.current === detectedOsTypeId) {
      return;
    }

    autoSavedDetectedOsTypeIdRef.current = detectedOsTypeId;
    void saveUserSettings(UserSettingType.GamesPlatformOsType, String(detectedOsTypeId));
  }, [detectedOsTypeId, saveUserSettings, savedOsTypeId, sessionOsTypeId, userSettingsLoaded]);

  const setOsTypeId = useCallback(
    (nextOsTypeId: PlatformFilterOsType) => {
      setSessionOsTypeId(nextOsTypeId);
      setLocalOsTypeId(nextOsTypeId);
      void saveUserSettings(UserSettingType.GamesPlatformOsType, String(nextOsTypeId));
    },
    [saveUserSettings]
  );

  const shouldShowPlatformPrompt =
    userSettingsLoaded &&
    onboardingCompleted &&
    savedOsTypeId == null &&
    sessionOsTypeId == null &&
    detectedOsTypeId == null;

  return {
    osTypeId,
    setOsTypeId,
    shouldShowPlatformPrompt,
  };
};
